/* eslint-env browser */
/* tslint:disable:max-line-length */

// Vendor
import { inject } from 'mobx-react';
import { RouteComponentProps, withRouter } from 'react-router-dom';
import * as React from 'react';

import { AnalyticsStore, ExperimentStore, PageStore } from 'bernie-core';
import { Context } from 'bernie-context';
import { ErrorBoundary } from 'bernie-view';
import { LocalizedComponentProps, withLocalization } from 'bernie-l10n';

// Bernie
import { Experiment, ExperimentControl, ExperimentVariant } from '@shared-ui/experiment-context';

// Stores
import { ContextPair, MultiContextProvider, SearchCriteriaContext } from 'stores/contexts';
import { MultiItemStore } from 'stores/multi-item-store';
import { PlatformContext } from 'src/common/stores/contexts/platformContext';
import { SearchCriteriaModel } from 'stores/models/search-criteria-model';

// Components
import { Footer } from '@gc/global-components';
// Controllers
import { HotelHomeController } from 'controllers/hotel/hotel-home-controller';
import { AnimatedProgressIndicator } from 'components/common/animated-progress-indicator';
import { BodyRegion } from './body-region';
import { Header } from 'components/common/header/header';
import { HeaderRegion } from './header-region';
import { InfositeController } from 'controllers/hotel/infosite-controller';
import { LodgingPwaPropertySearch } from '__generated__/api';
import { MainRegion } from './main-region';
import { MesoScripts } from 'components/meso/internals/meso-scripts';
import { NavigationRegion } from './navigation-region';
import { Notification } from 'components/common/notification';
import { PwaController } from 'controllers/admin/pwa-controller';
import { SearchController } from 'controllers/hotel/search-controller';
import { SearchNavigationTab } from 'components/search/search-navigation-tab';
import { SideRailRegion } from './side-rail-region';
import { ToolbarRegion } from './toolbar-region';
import * as invariant from 'invariant';

import NavigationTabs = LodgingPwaPropertySearch.NavigationTabs;

const toolbarTypes = {
  FIXED_FLEXIBLE_WATERFALL: 'pwa-toolbar__fixed-flexible-waterfall pwa-toolbar',
  NORMAL: 'pwa-toolbar__normal pwa-toolbar',
  TRANSPARENT_OVERLAY_WITH_HEADER: 'pwa-toolbar__transparent-overlay-with-header pwa-toolbar',
  TRANSPARENT_OVERLAY: 'pwa-toolbar__transparent-overlay pwa-toolbar',
};

export const sideRailTypes = {
  NEXT_TO_MAIN_AND_TOOLBAR: 'NEXT_TO_MAIN_AND_TOOLBAR',
  NEXT_TO_MAIN: 'NEXT_TO_MAIN',
  NONE: 'NONE',
};

export type MasterPageRoute = HotelHomeController | InfositeController | PwaController | SearchController;

export interface MasterPageProps extends LocalizedComponentProps, Partial<RouteComponentProps> {
  analytics?: AnalyticsStore;
  className?: string;
  context?: Context;
  dataStid?: string;
  page?: PageStore;
  pageHeaderClassName?: string;
  route?: MasterPageRoute;
  searchCriteria?: SearchCriteriaModel;
  sideRailType?: string;
  toolbarType?: string;
  multiItem?: MultiItemStore;
  pageHeader?: string;
  experiment?: ExperimentStore;
  navigationTabs?: NavigationTabs;
}

/**
 * MasterPage - stateless component for defining a reusable page structure with specific regions of content
 *
 * @param this.props {object} - the standard this.props object passed by React
 * @returns {string} - populated HTML of all applicable regions and wrapped in a div
 */
@withRouter
@withLocalization
@inject('analytics', 'context', 'experiment', 'page', 'multiItem', 'searchCriteria')
export class MasterPage extends React.Component<MasterPageProps> {
  public static sideRailTypes = sideRailTypes;
  public static toolbarTypes = toolbarTypes;

  public static defaultProps = {
    sideRailType: sideRailTypes.NONE,
    toolbarType: toolbarTypes.NORMAL,
    l10n: null,
  };
  static wrappedComponent: any;

  constructor(props) {
    super(props);

    if (this.props.analytics && this.props.route && this.props.route.routeData) {
      const routeData = this.props.route.routeData;

      this.props.analytics.setPageNamespace(routeData.rfrrNamespace || '');
      // nested the above if statement instead of appending to outer ones so we can start handling other routeData here

      if (this.props.page && !this.props.page.pageName) {
        this.props.page.pageName = routeData.pageName || 'page.unknown';
      }
    }

    this.setPageTitle = this.setPageTitle.bind(this);
  }

  public componentDidMount() {
    this.setPageTitle(this.props.page && this.props.page.title);
  }

  public componentWillUpdate(nextProps) {
    this.setPageTitle(nextProps.page && nextProps.page.title);
  }

  public setPageTitle(title) {
    document.title = title || this.props.l10n.formatText('common.defaultPageTitle');
  }

  public render() {
    const { context, dataStid, pageHeaderClassName, searchCriteria, pageHeader, navigationTabs } = this.props;

    const headerDefault = <Header className={pageHeaderClassName} searchCriteria={searchCriteria} />;

    let headerContent = null;
    let mainContent: React.ReactElement<Record<string, unknown>> | undefined = null;
    let sideRailContent: React.ReactElement<Record<string, unknown>> | undefined = null;
    let toolbarContent: React.ReactElement<Record<string, unknown>> | undefined = null;
    let navigationContent: React.ReactElement<Record<string, unknown>> | undefined = null;

    React.Children.forEach(this.props.children, child => {
      if (React.isValidElement(child)) {
        invariant(
          child.type === HeaderRegion ||
            child.type === MainRegion ||
            child.type === SideRailRegion ||
            child.type === ToolbarRegion ||
            child.type === NavigationRegion,
          `MasterPage: child type "${(child.type && !(typeof child.type === 'string') && child.type.name) ||
            child.type}" is invalid. Can only contain children of type ["HeaderRegion", "MainRegion", "ToolbarRegion", "NavigationRegion"]`
        );

        switch (child.type) {
          case HeaderRegion:
            invariant(headerContent === null, 'MasterPage: can only contain one child of type "HeaderRegion"');
            headerContent = child;
            break;
          case MainRegion:
            invariant(mainContent === null, 'MasterPage: can only contain one child of type "MainRegion"');
            mainContent = child;
            break;
          case SideRailRegion:
            invariant(sideRailContent === null, 'MasterPage: can only contain one child of type "SideRailRegion"');
            sideRailContent = child;
            break;
          case ToolbarRegion:
            invariant(toolbarContent === null, 'MasterPage: can only contain one child of type "ToolbarRegion"');
            toolbarContent = child;
            break;
          case NavigationRegion:
            invariant(navigationContent === null, 'MasterPage: can only contain one child of type "NavigationRegion"');
            navigationContent = child;
            break;
          default:
            break;
        }
      }
    });

    const headerClassName = ['pwa-toolbar', 'pwa-toolbar-header', this.props.toolbarType].filter(val => val).join(' ');
    const headerContentRegion = headerContent && <header className={headerClassName}>{headerContent}</header>;

    const contextPairs: ContextPair[] = [
      { context: PlatformContext, value: context },
      { context: SearchCriteriaContext, value: searchCriteria },
    ];

    const pageContainerRegionProps = {
      dataStid,
      headerContentRegion,
      headerDefault,
      toolbarType: this.props.toolbarType,
      sideRailType: this.props.sideRailType,
      toolbarContent,
      mainContent,
      sideRailContent,
      navigationContent,
      pageHeader,
      navigationTabs,
    };

    return (
      <MultiContextProvider contextPairs={contextPairs}>
        <ErrorBoundary label="hotel.masterPage.renderToNodeStreamTest">
          <Experiment name="PWA_RENDER_TO_NODE_STREAM">
            <ExperimentControl />
            <ExperimentVariant bucket={1} />
          </Experiment>
        </ErrorBoundary>
        <PageContainerRegion {...pageContainerRegionProps} containerClass={`page-container ${this.props.className}`} />
        <ErrorBoundary label="meso.scripts.errorboundary">
          <MesoScripts />
        </ErrorBoundary>
      </MultiContextProvider>
    );
  }
}

const PageContainerRegion = ({
  dataStid,
  headerContentRegion,
  headerDefault,
  toolbarType,
  sideRailType,
  toolbarContent,
  mainContent,
  sideRailContent,
  navigationContent,
  pageHeader,
  containerClass,
  navigationTabs,
}) => (
  <div className={containerClass} data-stid={dataStid}>
    <ErrorBoundary label="hotel.masterPage.header">{headerContentRegion || headerDefault}</ErrorBoundary>
    <main>
      {navigationTabs && <SearchNavigationTab navigationBarTabList={navigationTabs} />}
      <BodyRegion
        mainContent={mainContent}
        toolbarContent={toolbarContent}
        sideRailContent={sideRailContent}
        toolbarType={toolbarType}
        sideRailType={sideRailType}
        navigationContent={navigationContent}
        pageHeader={pageHeader}
      />
    </main>
    <Footer />
    <AnimatedProgressIndicator />
    <Notification />
  </div>
);
